import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../../ThemeContext';
import { useCloudflareCall } from '../../hooks/useCloudflareCall';
import { useParams } from 'react-router-dom';

interface Model {
  id: string;
  name: string;
}

interface Voice {
  id: string;
  name: string;
  value: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

interface ThemeContextType {
  theme: 'light' | 'dark';
}

// Available models and voices
const AVAILABLE_MODELS: Model[] = [
  { id: 'gpt-4o-realtime-preview-2024-12-17', name: 'GPT-4 Realtime' },
  { id: 'gpt-4o-mini-realtime-preview-2024-12-17', name: 'GPT-4 Mini Realtime' }
];

const VOICE_OPTIONS: Voice[] = [
  { id: 'alloy', name: 'Alloy (Neutral)', value: 'alloy' },
  { id: 'echo', name: 'Echo (Male)', value: 'echo' },
  { id: 'fable', name: 'Fable (Expressive)', value: 'fable' },
  { id: 'onyx', name: 'Onyx (Male)', value: 'onyx' },
  { id: 'nova', name: 'Nova (Female)', value: 'nova' },
  { id: 'shimmer', name: 'Shimmer (Female)', value: 'shimmer' }
];

const CallPage: React.FC = () => {
  const { theme } = useTheme() as ThemeContextType;
  const { id } = useParams<{ id: string }>();
  const scenarioId = id ? parseInt(id, 10) : undefined;
  const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>(AVAILABLE_MODELS[0].id);
  const [selectedVoice, setSelectedVoice] = useState<string>('alloy');
  const [currentAIResponse, setCurrentAIResponse] = useState<string>('');
  const [lastCompleteAIResponse, setLastCompleteAIResponse] = useState<string>('');
  const currentResponseRef = useRef<string>('');

  // Add missing refs with proper types
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const { isInCall, isTalking, startCall, endCall } = useCloudflareCall({
    scenarioId,
    voice: selectedVoice,
    modelVersion: selectedModel,
    onTextReceived: (text) => {
      console.log('[CallPage] Received text:', text);
      // Clear current response when new text is received
      setCurrentAIResponse('');
    },
    onTextDelta: (delta) => {
      currentResponseRef.current += delta;
      setCurrentAIResponse(currentResponseRef.current);
    },
    onTextComplete: () => {
      console.log('[CallPage] AI response complete:', currentResponseRef.current);
      setLastCompleteAIResponse(currentResponseRef.current);
      currentResponseRef.current = '';
    },
    onError: (error) => {
      console.error('[CallPage] Cloudflare Calls error:', error);
    }
  });

  // Clear text when call ends
  useEffect(() => {
    if (!isInCall) {
      console.log('[CallPage] Call ended, clearing text');
      setCurrentAIResponse('');
      setLastCompleteAIResponse('');
      currentResponseRef.current = '';
    }
  }, [isInCall]);

  // Improved audio visualization setup
  useEffect(() => {
    const cleanupAudio = () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
      audioContextRef.current = null;
      analyserRef.current = null;
      setIsUserSpeaking(false);
    };

    if (isInCall) {
      const setupAudioVisualization = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          
          // Create audio context and analyser
          audioContextRef.current = new AudioContext();
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.minDecibels = -50;  // Increased sensitivity
          analyserRef.current.maxDecibels = -10;
          analyserRef.current.smoothingTimeConstant = 0.5;  // Faster response
          
          const source = audioContextRef.current.createMediaStreamSource(stream);
          source.connect(analyserRef.current);
          
          // Set up frequency analysis
          analyserRef.current.fftSize = 256;  // Smaller FFT for faster response
          const bufferLength = analyserRef.current.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          
          let speakingFrames = 0;
          const SPEAKING_THRESHOLD = 30;  // Adjusted threshold
          const FRAMES_TO_CONFIRM = 2;  // Faster response

          const checkAudio = () => {
            if (analyserRef.current && audioContextRef.current?.state === 'running') {
              analyserRef.current.getByteFrequencyData(dataArray);
              
              // Calculate average frequency
              const average = dataArray.reduce((a, b) => a + b) / bufferLength;
              
              // Use a frame counter to smooth out the visualization
              if (average > SPEAKING_THRESHOLD) {
                speakingFrames++;
                if (speakingFrames >= FRAMES_TO_CONFIRM && !isUserSpeaking) {
                  setIsUserSpeaking(true);
                }
              } else {
                speakingFrames = Math.max(0, speakingFrames - 1);  // Gradual decrease
                if (speakingFrames === 0 && isUserSpeaking) {
                  setIsUserSpeaking(false);
                }
              }
              
              animationFrameRef.current = requestAnimationFrame(checkAudio);
            }
          };

          checkAudio();
        } catch (error) {
          console.error('Error setting up audio visualization:', error);
        }
      };

      setupAudioVisualization();
    }

    return cleanupAudio;
  }, [isInCall, isUserSpeaking]);

  const getBoxStyle = (isActive: boolean) => ({
    width: '400px',
    height: '200px',
    border: '2px solid',
    borderColor: theme === 'dark' ? '#444' : '#ddd',
    borderRadius: '10px',
    transition: 'all 0.3s ease',
    backgroundColor: isActive && !isTalking 
      ? (theme === 'dark' ? '#1e4620' : '#e8f5e9')  // Only glow for user when speaking
      : (theme === 'dark' ? '#2a2a2a' : '#f8f9fa'),
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 20px',
    position: 'relative' as const
  });

  const getSelectStyle = () => ({
    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#ffffff',
    color: theme === 'dark' ? '#ffffff' : '#000000',
    border: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`,
    borderRadius: '5px',
    padding: '8px',
    margin: '0 10px',
    minWidth: '200px'
  });

  return (
    <div className={`container ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'} min-vh-100 p-4`}>
      <div className="text-center mb-4">
        <h1>OpenAI Realtime Demo</h1>
      </div>

      <div className="d-flex justify-content-center align-items-center mb-4">
        <select
          style={getSelectStyle()}
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          disabled={isInCall}
        >
          {AVAILABLE_MODELS.map(model => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>

        <select
          style={getSelectStyle()}
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          disabled={isInCall}
        >
          {VOICE_OPTIONS.map(voice => (
            <option key={voice.id} value={voice.value}>
              {voice.name}
            </option>
          ))}
        </select>
      </div>

      <div className="d-flex justify-content-center mb-4">
        <button
          className={`btn ${isInCall ? 'btn-danger' : 'btn-success'} btn-lg`}
          onClick={isInCall ? endCall : startCall}
          disabled={!scenarioId}
        >
          {isInCall ? 'End Call' : 'Start Call'}
        </button>
      </div>

      <div className="d-flex justify-content-center mb-4">
        <div style={getBoxStyle(isUserSpeaking)}>
          <div className="text-center">
            <h4>You</h4>
            {isUserSpeaking && <div className="text-success">Speaking...</div>}
          </div>
        </div>
        <div style={getBoxStyle(isTalking)}>
          <div className="text-center">
            <h4>AI Assistant</h4>
            {isTalking && <div className="text-success">Speaking...</div>}
          </div>
        </div>
      </div>

      <div className="text-center">
        <h4>Current Response:</h4>
        <p>{currentAIResponse}</p>
        <h4>Last Complete Response:</h4>
        <p>{lastCompleteAIResponse}</p>
      </div>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CallPage;
